import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Flex,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  useDisclosure
} from '@chakra-ui/react';
import { FaDownload, FaSearch, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { MdEmail, MdError, MdBlock, MdCheck, MdClose } from 'react-icons/md';
import api, { getToken, API_BASE_URL } from '../../../../../../services/api';

const UndeliveredReportModal = ({ isOpen, onClose, campaignId, campaignName }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = delivered, 1 = undelivered
  const [activeTypeTab, setActiveTypeTab] = useState(0); // 0 = OAB, 1 = CPF, 2 = CNPJ
  const [exporting, setExporting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && campaignId) {
      fetchUnifiedData();
    }
  }, [isOpen, campaignId]);

  const fetchUnifiedData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/analytics/campaigns/${campaignId}/undelivered`);
      
      if (response.success) {
        setData(response);
        // Set initial tab to show undelivered if there are any
        if (response.summary.documentsWithoutDelivery > 0) {
          setActiveTab(1);
        }
      }
    } catch (error) {
      console.error('Error fetching unified data:', error);
      toast({
        title: 'Erro ao carregar dados',
        description: 'Não foi possível carregar o relatório de documentos',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (category = 'all', type = null) => {
    setExporting(true);
    try {
      let url = `${API_BASE_URL}/analytics/campaigns/${campaignId}/undelivered/export?category=${category}`;
      if (type) {
        url += `&type=${type}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const categoryLabel = category === 'delivered' ? 'entregues' : category === 'undelivered' ? 'nao-entregues' : 'todos';
      const filename = `documentos-${categoryLabel}-${campaignId}${type ? `-${type}` : ''}-${Date.now()}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Relatório exportado',
        description: 'O arquivo CSV foi baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({
        title: 'Erro ao exportar',
        description: 'Não foi possível exportar o relatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };

  const filterDocuments = (documents) => {
    return documents.filter(doc => {
      const matchesSearch = !searchTerm || 
        doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.identifiers?.some(id => id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        doc.emails?.some(e => e.email.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesSearch;
    });
  };

  const DocumentRow = ({ document }) => {
    const { isOpen, onToggle } = useDisclosure();
    
    return (
      <>
        <Tr 
          cursor="pointer" 
          onClick={onToggle}
          _hover={{ bg: 'gray.50' }}
        >
          <Td>
            <HStack spacing={2}>
              <IconButton
                icon={isOpen ? <FaChevronUp /> : <FaChevronDown />}
                size="xs"
                variant="ghost"
                aria-label="Expandir"
              />
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium">{document.name}</Text>
                <Text fontSize="xs" color="gray.500">
                  {document.identifiers?.length || document.totalIdentifiers || 1} identificador(es)
                </Text>
              </VStack>
            </HStack>
          </Td>
          <Td>
            <Text fontSize="sm" noOfLines={2}>
              {document.identifiers?.slice(0, 2).join(', ')}
              {document.identifiers?.length > 2 && ` +${document.identifiers.length - 2}`}
            </Text>
          </Td>
          <Td>
            <HStack spacing={2}>
              <Badge colorScheme={document.deliveredCount > 0 ? "green" : "red"}>
                {document.deliveredCount}/{document.totalEmails} entregues
              </Badge>
            </HStack>
          </Td>
          <Td isNumeric>{document.precatoriosCount}</Td>
        </Tr>
        <Tr>
          <Td colSpan={4} p={0}>
            <Collapse in={isOpen} animateOpacity>
              <Box p={4} bg="gray.50">
                <VStack align="stretch" spacing={3}>
                  {/* Show all identifiers */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>Identificadores:</Text>
                    <Flex flexWrap="wrap" gap={2} pl={4}>
                      {document.identifiers?.map((id, idx) => (
                        <Badge key={idx} colorScheme="blue" variant="outline">
                          {id}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                  
                  <Divider />
                  
                  {/* Show all emails grouped by identifier */}
                  <Box>
                    <Text fontSize="sm" fontWeight="bold">Emails ({document.totalEmails}):</Text>
                    {document.emails.map((email, idx) => (
                      <HStack key={idx} pl={4} py={1}>
                        {email.delivered ? (
                          <MdCheck color="green" />
                        ) : (
                          <MdClose color="red" />
                        )}
                        <Text fontSize="sm">{email.email}</Text>
                        {email.identifier && (
                          <Text fontSize="xs" color="gray.500">({email.identifier})</Text>
                        )}
                        <Badge size="sm" colorScheme={email.delivered ? "green" : "red"}>
                          {email.delivered ? "Entregue" : email.status}
                        </Badge>
                      </HStack>
                    ))}
                  </Box>
                  
                  {document.failedEmails?.length > 0 && (
                    <>
                      <Divider />
                      <Text fontSize="sm" fontWeight="bold" color="red.600">
                        Resumo de Falhas: {document.failedEmails.length} email(s) não entregue(s)
                      </Text>
                    </>
                  )}
                </VStack>
              </Box>
            </Collapse>
          </Td>
        </Tr>
      </>
    );
  };

  const DocumentTable = ({ documents, type, category }) => {
    const filteredDocuments = filterDocuments(documents);

    return (
      <Box>
        <Flex mb={4} gap={4} align="center">
          <InputGroup maxW="400px">
            <InputLeftElement pointerEvents="none">
              <FaSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por identificador, nome ou email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <Button
            leftIcon={<FaDownload />}
            colorScheme="green"
            size="sm"
            onClick={() => exportReport(category, type)}
            isLoading={exporting}
            ml="auto"
          >
            Exportar {type.toUpperCase()}
          </Button>
        </Flex>

        <Box maxH="400px" overflowY="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr>
                <Th width="35%">Nome</Th>
                <Th width="30%">Identificadores ({type.toUpperCase()})</Th>
                <Th width="25%">Status Emails</Th>
                <Th width="10%" isNumeric>Precatórios</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredDocuments.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center" py={8}>
                    <Text color="gray.500">
                      {searchTerm 
                        ? 'Nenhum resultado encontrado com os filtros aplicados' 
                        : 'Nenhum documento nesta categoria'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredDocuments.map((doc, idx) => (
                  <DocumentRow key={doc.identifier + idx} document={doc} />
                ))
              )}
            </Tbody>
          </Table>
        </Box>
        
        <Text mt={2} fontSize="sm" color="gray.600">
          Mostrando {filteredDocuments.length} de {documents.length} documentos
        </Text>
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw">
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>Relatório Unificado de Documentos</Text>
            <Text fontSize="sm" fontWeight="normal" color="gray.600">
              Campanha: {campaignName}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <Spinner size="xl" color="blue.500" />
            </Box>
          ) : data ? (
            <VStack spacing={6} align="stretch">
              {/* Summary Statistics */}
              <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4}>
                <Stat bg="blue.50" p={4} borderRadius="md">
                  <StatLabel>Total Documentos</StatLabel>
                  <StatNumber color="blue.600">
                    {data.summary.totalDocuments}
                  </StatNumber>
                </Stat>
                
                <Stat bg="green.50" p={4} borderRadius="md">
                  <StatLabel>Com Entrega</StatLabel>
                  <StatNumber color="green.600">
                    {data.summary.documentsWithDelivery}
                  </StatNumber>
                </Stat>
                
                <Stat bg="red.50" p={4} borderRadius="md">
                  <StatLabel>Sem Entrega</StatLabel>
                  <StatNumber color="red.600">
                    {data.summary.documentsWithoutDelivery}
                  </StatNumber>
                </Stat>
                
                <Stat bg="purple.50" p={4} borderRadius="md">
                  <StatLabel>Total Emails</StatLabel>
                  <StatNumber color="purple.600">
                    {data.summary.totalEmails}
                  </StatNumber>
                </Stat>
                
                <Stat bg="orange.50" p={4} borderRadius="md">
                  <StatLabel>Taxa Entrega</StatLabel>
                  <StatNumber color="orange.600">
                    {data.summary.totalEmails > 0 
                      ? Math.round((data.summary.totalDelivered / data.summary.totalEmails) * 100)
                      : 0}%
                  </StatNumber>
                </Stat>
              </SimpleGrid>

              {/* Status Info */}
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Resumo por Tipo de Documento</AlertTitle>
                  <AlertDescription>
                    <SimpleGrid columns={3} spacing={4} mt={2}>
                      {['oab', 'cpf', 'cnpj'].map(type => (
                        <HStack key={type}>
                          <Text fontWeight="bold">{type.toUpperCase()}:</Text>
                          <Badge colorScheme="green">
                            {data.summary.byType[type].delivered} entregues
                          </Badge>
                          <Badge colorScheme="red">
                            {data.summary.byType[type].undelivered} não entregues
                          </Badge>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Main Tabs - Delivered vs Undelivered */}
              <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
                <TabList>
                  <Tab>
                    <HStack>
                      <FaCheckCircle color="green" />
                      <Text>Documentos com Entrega ({data.summary.documentsWithDelivery})</Text>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack>
                      <FaTimesCircle color="red" />
                      <Text>Documentos sem Entrega ({data.summary.documentsWithoutDelivery})</Text>
                    </HStack>
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Delivered Documents */}
                  <TabPanel>
                    <Tabs index={activeTypeTab} onChange={setActiveTypeTab} variant="soft-rounded" colorScheme="green">
                      <TabList mb={4}>
                        <Tab>OAB ({data.deliveredDocuments.oab.length})</Tab>
                        <Tab>CPF ({data.deliveredDocuments.cpf.length})</Tab>
                        <Tab>CNPJ ({data.deliveredDocuments.cnpj.length})</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel>
                          <DocumentTable 
                            documents={data.deliveredDocuments.oab} 
                            type="oab"
                            category="delivered"
                          />
                        </TabPanel>
                        <TabPanel>
                          <DocumentTable 
                            documents={data.deliveredDocuments.cpf} 
                            type="cpf"
                            category="delivered"
                          />
                        </TabPanel>
                        <TabPanel>
                          <DocumentTable 
                            documents={data.deliveredDocuments.cnpj} 
                            type="cnpj"
                            category="delivered"
                          />
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </TabPanel>
                  
                  {/* Undelivered Documents */}
                  <TabPanel>
                    <Tabs index={activeTypeTab} onChange={setActiveTypeTab} variant="soft-rounded" colorScheme="red">
                      <TabList mb={4}>
                        <Tab>OAB ({data.undeliveredDocuments.oab.length})</Tab>
                        <Tab>CPF ({data.undeliveredDocuments.cpf.length})</Tab>
                        <Tab>CNPJ ({data.undeliveredDocuments.cnpj.length})</Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel>
                          <DocumentTable 
                            documents={data.undeliveredDocuments.oab} 
                            type="oab"
                            category="undelivered"
                          />
                        </TabPanel>
                        <TabPanel>
                          <DocumentTable 
                            documents={data.undeliveredDocuments.cpf} 
                            type="cpf"
                            category="undelivered"
                          />
                        </TabPanel>
                        <TabPanel>
                          <DocumentTable 
                            documents={data.undeliveredDocuments.cnpj} 
                            type="cnpj"
                            category="undelivered"
                          />
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          ) : (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Erro ao carregar dados</AlertTitle>
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              leftIcon={<FaDownload />}
              colorScheme="blue"
              onClick={() => exportReport('all')}
              isLoading={exporting}
              isDisabled={!data}
            >
              Exportar Todos
            </Button>
            <Button
              leftIcon={<FaCheckCircle />}
              colorScheme="green"
              onClick={() => exportReport('delivered')}
              isLoading={exporting}
              isDisabled={!data || data.summary.documentsWithDelivery === 0}
            >
              Exportar Entregues
            </Button>
            <Button
              leftIcon={<FaTimesCircle />}
              colorScheme="red"
              onClick={() => exportReport('undelivered')}
              isLoading={exporting}
              isDisabled={!data || data.summary.documentsWithoutDelivery === 0}
            >
              Exportar Não Entregues
            </Button>
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UndeliveredReportModal;